/**
 * API function to fetch available batches for Mathlete 101 course
 * Based on starry-night reference implementation
 */

import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { MATHLETE_CONFIG } from '@/features/user-notifications/utils/constants';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { MathleteBatch, MathleteBatchesResponse } from '../types';

export interface GetMathleteBatchesParams {
  profile_id: string;
  variant_id: string;
  product_id?: string;
  grade?: string;
}

/**
 * Fetches available batches for Mathlete course by variant ID
 *
 * @param params - Parameters for batch fetching
 * @returns Promise resolving to batches response
 *
 * @example
 * ```ts
 * const batches = await getMathleteBatches({
 *   profile_id: 'user-123',
 *   variant_id: 'variant-456'
 * });
 * ```
 */
export const getMathleteBatches = async (
  params: GetMathleteBatchesParams
): Promise<MathleteBatchesResponse> => {
  const response = await axios.get<MathleteBatchesResponse>(
    MATHLETE_CONFIG.apiEndpoints.getBatches,
    {
      params,
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return response;
};

/**
 * Generate mock Mathlete batch data with realistic dates and times
 */
const generateMathleteBatch = (
  index: number,
  variantId: string
): MathleteBatch => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 7 + index * 7);

  // Different schedule patterns to match real API format (3-letter day abbreviations)
  const schedulePatterns = [
    [
      { day: 'Mon', time: '18:00' },
      { day: 'Wed', time: '18:00' },
      { day: 'Fri', time: '18:00' },
    ],
    [
      { day: 'Tue', time: '17:00' },
      { day: 'Thu', time: '17:00' },
      { day: 'Sat', time: '17:00' },
    ],
    [
      { day: 'Sat', time: '18:00' },
      { day: 'Sun', time: '18:00' },
      { day: 'Fri', time: '18:00' },
    ],
  ];

  const schedule = schedulePatterns[index % schedulePatterns.length];

  return {
    batch_id: `mathlete-batch-${index + 1}-${faker.string.alphanumeric(6)}`,
    variant_id: variantId,
    product_id: 'mathlete-101-product-id',
    start_date: startDate.toISOString().split('T')[0], // Format as YYYY-MM-DD to match real API
    start_time: schedule[0].time, // Use the time from schedule
    start_timestamp: startDate.getTime() / 1000, // Convert to seconds to match real API
    timezone: 'Asia/Kolkata',
    name: `Mathlete 101 - Batch ${index + 1}`,
    status: 'active',
    capacity: 1000,
    allowed_countries: ['IN'],
    not_allowed_countries: null,
    created_by: 'admin-user-id',
    created_at: faker.date.past().getTime().toString(),
    modified_at: null,
    modified_by: null,
    practice_batch: null,
    course_session_info: {
      teacher_id: null,
      moderator_id: null,
      num_sessions: 12,
      platform: '100ms',
      duration: 55,
      schedule,
    },
    practice_session_info: {
      teacher_id: null,
      moderator_id: null,
      num_sessions: 0,
      platform: 'zoom',
      duration: 0,
      schedule: [],
    },
  };
};

/**
 * MSW handler for fetching Mathlete batches
 */
export const getMathleteBatchesMockHandler = http.get(
  `${env.API_URL}/streams/v1/variant/batch`,
  async ({ request }) => {
    await networkDelay();

    const url = new URL(request.url);
    const profileId = url.searchParams.get('profile_id');
    const variantId = url.searchParams.get('variant_id');
    const grade = url.searchParams.get('grade');
    const productId = url.searchParams.get('product_id');

    if (grade && productId) {
      return void 0;
    }

    // Validate required params for Mathlete
    if (!profileId || !variantId) {
      return HttpResponse.json(
        {
          status: 'error',
          message: 'Missing required parameters: profile_id and variant_id',
        },
        { status: 400 }
      );
    }

    // Generate 5 mock batches with different time slots for variety
    const batches = Array.from({ length: 5 }, (_, index) =>
      generateMathleteBatch(index, variantId)
    );

    const response: MathleteBatchesResponse = {
      variant_info: {
        id: variantId,
        name: 'Mathlete 101',
        internal_name: 'mathlete-101',
        description: 'Mathlete 101 - Math Olympiad preparation course',
        product_id: 'mathlete-101-product-id',
        course_id: 'mathlete-101-course-id',
        grade_group: '6-8',
        category: 'math',
        status: 'active',
        modified_by: 'admin-user-id',
        modified_at: faker.date.recent().toISOString(),
        config: {
          welcome_webinar: true,
          assessment: true,
          reminder_comms: true,
          practice_session: false,
          show_in_website: true,
        },
        pricing: [
          {
            country: 'US',
            currency: 'USD',
            base_price: '299',
            early_bird_price: '249',
            final_price: '249',
          },
          {
            country: 'IN',
            currency: 'INR',
            base_price: '9999',
            early_bird_price: '7999',
            final_price: '7999',
          },
        ],
      },
      batches,
    };

    return HttpResponse.json(response, { status: 200 });
  }
);
